import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  SELECTION_CHIP_CLASSES,
  SELECTION_CHIP_RAIL_CLASSES,
  SELECTION_CHIP_REMOVE_CLASSES,
} from '../selection.tokens';

import { type SelectionChipsProps } from './SelectionChips.types';

/**
 * The chosen values, still visible with the panel shut, each removable without
 * reopening it.
 *
 * A chip whose label is still being fetched carries `aria-busy` rather than a
 * spinner: the value is already correct and readable (the raw value is its own
 * fallback label), so a spinner would claim the row is unusable when it is not.
 */
export function SelectionChips({
  values,
  labelOf,
  isResolving,
  maxVisibleChips,
  isDisabled,
  onRemove,
}: SelectionChipsProps) {
  const { t } = useTranslation('uiKit');

  if (values.length === 0) return null;

  const visible = values.slice(0, maxVisibleChips);
  const overflow = values.length - visible.length;

  return (
    <ul className={SELECTION_CHIP_RAIL_CLASSES}>
      {visible.map((value) => (
        <li key={value} className="flex min-w-0">
          <span
            className={SELECTION_CHIP_CLASSES}
            aria-busy={isResolving?.(value) === true ? true : undefined}
          >
            <span className="truncate">{labelOf(value)}</span>
            {isDisabled ? null : (
              <button
                type="button"
                // Named with its own value, so a screen reader hears WHICH chip
                // this removes rather than four identical buttons.
                aria-label={t('multiSelect.remove', { label: labelOf(value) })}
                onClick={() => {
                  onRemove(value);
                }}
                className={SELECTION_CHIP_REMOVE_CLASSES}
              >
                <X aria-hidden className="h-3 w-3" />
              </button>
            )}
          </span>
        </li>
      ))}
      {overflow > 0 ? (
        <li className="self-center text-caption text-muted-foreground">
          {t('multiSelect.more', { count: overflow })}
        </li>
      ) : null}
    </ul>
  );
}
